import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import {
  Box,
  Button,
  Alert,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { changePasswordEmail } from "../../actions/auth";
import rutas from "../../routes/constants/routes";
import MyUserService from "../../services/myUser";
import TokenService from "../../services/token";
import React from "react";
import {
  FormErrorsConfirmationCode,
  FormTouchedConfirmationCode,
  FormValuesConfirmationCode,
  PropsConfirmationCode,
  StatePropsConfirmationCode,
} from "../../interface/changePassword";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;

const passwordRules = {
  minLength: 6,
  hasUppercase: /[A-Z]/,
  hasSpecialChar: /[^A-Za-z0-9]/,
};

const ChangePasswordEmail = (props: PropsConfirmationCode) => {
  const { Email, Code } = useParams<{ Email: string; Code: string }>();

  const myUser = MyUserService.getMyUser();
  const hasSession = TokenService.hasSession();
  const email = hasSession ? myUser.email : Email;
  const history = useHistory();

  const [values, setValues] = useState<FormValuesConfirmationCode>({
    NewPassword: "",
    PasswordCopy: "",
    Email: email ?? "",
    Password: "",
    ConfirmationCode: Code ?? "",
    ChangePassword: false,
  });

  const [errors, setErrors] = useState<FormErrorsConfirmationCode>({});
  const [touched, setTouched] = useState<FormTouchedConfirmationCode>({});

  useEffect(() => {
    if (props.successPasswordEmail) {
      history.push(rutas.LOGIN);
      window.location.reload();
    }
  }, [props.successPasswordEmail]);

  const evaluatePassword = (password: string) => {
    const checks = {
      minLength: password.length >= passwordRules.minLength,
      hasUppercase: passwordRules.hasUppercase.test(password),
      hasSpecialChar: passwordRules.hasSpecialChar.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;

    return { checks, score };
  };

  const passwordEvaluation = React.useMemo(
    () => evaluatePassword(values.NewPassword),
    [values.NewPassword],
  );

  const getStrengthColor = (score: number) => {
    switch (score) {
      case 1:
        return "error";
      case 2:
        return "warning";
      case 3:
        return "success";
      default:
        return "error";
    }
  };

  const validate = (
    values: FormValuesConfirmationCode,
    hasSession: boolean,
  ): FormErrorsConfirmationCode => {
    const errors: FormErrorsConfirmationCode = {};

    if (hasSession && !values.Password) {
      errors.Password = "Ingrese su contraseña actual";
    }

    if (!values.NewPassword) {
      errors.NewPassword = "Ingrese una contraseña";
    } else if (!PASSWORD_REGEX.test(values.NewPassword)) {
      errors.NewPassword =
        "Debe tener: 1 mayúscula, 1 número, 1 caracter especial y una longitud mínima de 8";
    }

    if (!values.PasswordCopy) {
      errors.PasswordCopy = "Ingrese nuevamente la contraseña";
    } else if (values.PasswordCopy !== values.NewPassword) {
      errors.PasswordCopy = "Las contraseñas deben coincidir";
    }

    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updated = { ...values, [name]: value };
    setValues(updated);

    if (touched[name as keyof FormTouchedConfirmationCode]) {
      setErrors(validate(updated, hasSession));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(values, hasSession));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({ Password: true, NewPassword: true, PasswordCopy: true });
    const validationErrors = validate(values, hasSession);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      props.changePasswordEmail(values);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, my: 4 }}>
      <Box sx={{ width: "100%", maxWidth: "30vw" }}>
        {/* Email info card */}
        <Typography variant="body1" align="center" sx={{ mb: 1 }}>
          Cambiar contraseña para el cliente con email:
        </Typography>
        <Paper
          elevation={0}
          sx={{
            bgcolor: "#e2e2e2",
            borderRadius: 2,
            p: 2,
            mb: 3,
            display: "flex",
            gap: 3,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Email
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={300}>
            {email}
          </Typography>
        </Paper>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {hasSession && (
            <TextField
              fullWidth
              margin="normal"
              id="Password"
              name="Password"
              label="Contraseña actual"
              type="password"
              value={values.Password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.Password && Boolean(errors.Password)}
              helperText={touched.Password && errors.Password}
            />
          )}

          <TextField
            fullWidth
            margin="normal"
            id="NewPassword"
            name="NewPassword"
            label="Nueva contraseña"
            type="password"
            value={values.NewPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.NewPassword && Boolean(errors.NewPassword)}
            helperText={
              touched.NewPassword && errors.NewPassword
                ? errors.NewPassword
                : "Debe tener: 1 mayúscula, 1 número, 1 caracter especial y una longitud mínima de 8."
            }
          />

          <TextField
            fullWidth
            margin="normal"
            id="PasswordCopy"
            name="PasswordCopy"
            label="Repetir contraseña"
            type="password"
            value={values.PasswordCopy}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.PasswordCopy && Boolean(errors.PasswordCopy)}
            helperText={touched.PasswordCopy && errors.PasswordCopy}
          />

          <LinearProgress
            variant="determinate"
            value={(passwordEvaluation.score / 3) * 100}
            color={getStrengthColor(passwordEvaluation.score)}
            sx={{ mt: 1, height: 6, borderRadius: 3 }}
          />

          <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
            Seguridad de la contraseña
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={props.loading}
              startIcon={
                props.loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              sx={{
                bgcolor: "#E3000B",
                px: 6,
                py: 1.5,
                "&:hover": { bgcolor: "#b8000a" },
                "&.Mui-disabled": { bgcolor: "#e57373", color: "white" },
              }}
            >
              Confirmar
            </Button>
          </Box>

          {props.errorPasswordEmail && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {props.errorPasswordEmail}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default connect(
  (state: {
    auth: {
      loading: boolean;
      successPasswordEmail: boolean;
      errorPasswordEmail: boolean;
      error: StatePropsConfirmationCode["error"];
    };
  }) => ({
    loading: state.auth.loading,
    successPasswordEmail: state.auth.successPasswordEmail,
    errorPasswordEmail: state.auth.errorPasswordEmail,
    error: state.auth.error,
  }),
  (dispatch) => ({
    changePasswordEmail: (data: FormValuesConfirmationCode) =>
      dispatch(changePasswordEmail(data)),
  }),
)(ChangePasswordEmail);
